import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { Calendar, User, ArrowLeft, Facebook, Linkedin } from "lucide-react";
import { SITE } from "@/data/site";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArtigoDetail = () => {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        setLoading(true);
        setNotFound(false);
        axios
            .get(`${API}/articles/${slug}`)
            .then((r) => setArticle(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));

        axios
            .get(`${API}/articles`)
            .then((r) => setRelated(r.data.filter((a) => a.slug !== slug).slice(0, 4)))
            .catch(() => {});
    }, [slug]);

    if (loading)
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center text-[color:var(--acd-muted)]">
                A carregar...
            </div>
        );
    if (notFound || !article)
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <h1 className="text-3xl font-bold mb-4">
                    Artigo não encontrado
                </h1>
                <Link to="/artigos" className="acd-btn-gradient">
                    Ver todos os artigos
                </Link>
            </div>
        );

    const shareUrl = typeof window !== "undefined" ? window.location.href : "";

    return (
        <div data-testid="page-article">
            <SEO
                title={article.title}
                description={article.excerpt}
                canonicalPath={`/artigos/${article.slug}`}
                image={article.image}
                type="article"
                articleData={article}
            />
            {/* Hero */}
            <section className="relative py-16 md:py-24 px-6 bg-[var(--acd-navy-900)] text-white overflow-hidden">
                <div
                    className="absolute inset-0 opacity-30 bg-cover bg-center"
                    style={{ backgroundImage: `url(${article.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--acd-navy-950)]/90 to-[var(--acd-navy-900)]/60" />
                <div className="relative z-10 max-w-4xl mx-auto">
                    <Link
                        to="/artigos"
                        className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8"
                    >
                        <ArrowLeft size={16} /> Voltar aos artigos
                    </Link>
                    <div>
                        <span className="acd-eyebrow">{article.category}</span>
                    </div>
                    <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mt-3 mb-6">
                        {article.title}
                    </h1>
                    <div className="flex flex-wrap gap-6 text-white/80 text-sm">
                        <span className="inline-flex items-center gap-2">
                            <User size={16} /> {article.author}
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <Calendar size={16} /> {article.date_display}
                        </span>
                    </div>
                </div>
            </section>

            {/* Body */}
            <section className="py-14 px-6">
                <div className="max-w-4xl mx-auto grid lg:grid-cols-[1fr_280px] gap-12">
                    <article className="acd-prose" data-testid="article-body">
                        <img
                            src={article.image}
                            alt={article.title}
                            className="w-full rounded-2xl mb-8 shadow-md"
                        />
                        <div
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    article.content_html,
                                ),
                            }}
                        />

                        <div className="mt-10 p-6 rounded-2xl bg-[var(--acd-bg-soft)] text-center">
                            <p className="text-[var(--acd-navy-900)] font-semibold mb-4">
                                Precisa de aconselhamento jurídico
                                especializado?
                            </p>
                            <a
                                href={SITE.whatsappUrlAppointment}
                                target="_blank"
                                rel="noreferrer"
                                className="acd-btn-gradient"
                            >
                                Falar com um Especialista
                            </a>
                        </div>

                        <div className="mt-8 pt-6 border-t border-gray-200 flex items-center gap-4">
                            <span className="text-sm font-semibold text-[var(--acd-navy-900)]">
                                Compartilhe este Artigo:
                            </span>
                            <a
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="w-9 h-9 rounded-full bg-[var(--acd-navy-900)] text-white flex items-center justify-center hover:bg-[var(--acd-blue-600)] transition"
                                aria-label="Partilhar no Facebook"
                            >
                                <Facebook size={16} />
                            </a>
                            <a
                                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="w-9 h-9 rounded-full bg-[var(--acd-navy-900)] text-white flex items-center justify-center hover:bg-[var(--acd-blue-600)] transition"
                                aria-label="Partilhar no LinkedIn"
                            >
                                <Linkedin size={16} />
                            </a>
                        </div>
                    </article>

                    {/* Sidebar */}
                    <aside className="space-y-8">
                        <div>
                            <h3 className="text-lg font-bold text-[var(--acd-navy-900)] mb-4">
                                Outros Artigos
                            </h3>
                            <div className="space-y-4">
                                {related.map((a) => (
                                    <Link
                                        to={`/artigos/${a.slug}`}
                                        key={a.slug}
                                        className="flex gap-3 items-start group"
                                    >
                                        <img
                                            src={a.image}
                                            alt=""
                                            className="w-20 h-20 rounded-lg object-cover shrink-0"
                                        />
                                        <div>
                                            <h4 className="text-sm font-semibold text-[var(--acd-navy-900)] group-hover:text-[var(--acd-blue-600)] leading-snug transition line-clamp-3">
                                                {a.title}
                                            </h4>
                                            <p className="text-xs text-[color:var(--acd-muted)] mt-1">
                                                {a.date_display}
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
};

export default ArtigoDetail;
